import { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';

const API_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : 'http://localhost:3000/api';

const SalaryBreakdownModal = ({ isOpen, onClose }) => {
  const [breakdown, setBreakdown] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());

  const token = localStorage.getItem('token');

  useEffect(() => {
    if (isOpen) {
      fetchBreakdown();
    }
  }, [isOpen, filterMonth, filterYear]);

  const fetchBreakdown = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/finance/salary-breakdown`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { month: filterMonth, year: filterYear }
      });
      setBreakdown(response.data);
    } catch (error) {
      console.error('Error fetching salary breakdown:', error);
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
      }
      Swal.fire('Error', 'Gagal memuat breakdown gaji', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const calculateTotalPaid = (payments) => {
    return payments.reduce((sum, payment) => sum + payment.amount, 0);
  };

  const calculatePercentage = (paid, total) => {
    if (total === 0) return 0;
    return ((paid / total) * 100).toFixed(1);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Breakdown Gaji per Channel</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            &times;
          </button>
        </div>

        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium mb-1">Bulan</label>
            <select
              value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)}
              className="border rounded px-3 py-2"
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                <option key={month} value={month}>
                  {new Date(2000, month - 1).toLocaleString('id-ID', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Tahun</label>
            <select
              value={filterYear}
              onChange={(e) => setFilterYear(e.target.value)}
              className="border rounded px-3 py-2"
            >
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8">Loading...</div>
        ) : breakdown.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            Tidak ada data gaji untuk periode ini
          </div>
        ) : (
          <div className="space-y-4">
            {breakdown.map((item, index) => {
              const totalPaid = calculateTotalPaid(item.payments);
              const remaining = item.total_salary - totalPaid;
              const percentage = calculatePercentage(totalPaid, item.total_salary);

              return (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="font-semibold text-lg">{item.officer_name}</h3>
                      <p className="text-sm text-gray-600">
                        {item.nip} | {item.position} | {item.department}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold">{formatCurrency(item.total_salary)}</p>
                      <p className="text-xs text-gray-500">Total Gaji</p>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-3">
                    <div className="flex justify-between text-xs mb-1">
                      <span>Terbayar: {percentage}%</span>
                      <span className={remaining > 0 ? 'text-red-600' : 'text-green-600'}>
                        {remaining > 0 ? `Sisa: ${formatCurrency(remaining)}` : 'Lunas'}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${percentage >= 100 ? 'bg-green-500' : 'bg-blue-500'
                          }`}
                        style={{ width: `${Math.min(percentage, 100)}%` }}
                      />
                    </div>
                  </div>

                  {/* Payment Details */}
                  {item.payments.length > 0 ? (
                    <div className="bg-gray-50 rounded p-3">
                      <p className="text-xs font-semibold text-gray-700 mb-2">Detail Pembayaran:</p>
                      <div className="space-y-1">
                        {item.payments.map((payment, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span className="text-gray-600">{payment.channel_name}</span>
                            <span className="font-medium">{formatCurrency(payment.amount)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">Belum ada pembayaran</p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-6 text-center">
          <button
            onClick={onClose}
            className="bg-gray-300 text-gray-700 px-6 py-2 rounded hover:bg-gray-400"
          >
            Tutup
          </button>
        </div>
      </div>
    </div>
  );
};

export default SalaryBreakdownModal;
